import {
  ActionModal,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  DateTimeField,
  FormSectionTitle,
  HelperText,
  TextField,
  Theme,
  Typography,
  ValueAutocompleteField,
  createStyles,
  makeStyles,
  useModal,
  useRouter,
  useSnackbar,
} from '@forgeglobal/design-framework'
import {
  BasicFundInfoFragment,
  FundingRound,
  OfferingDateDataFragment,
  OfferingDetailsFormInput,
  ValuationType,
  useAllOfferingDatesQuery,
  useGetAllEquifundsQuery,
  useGetOfferingQuery,
  useUnpublishOfferingMutation,
  useUpdateOfferingMutation,
} from 'graphql/bindings/AdminBindings'
import { Field, Form, FormRenderProps } from 'react-final-form'
import { MoneyFormattedInput, PercentFormattedInput } from 'components/shared/NumericInput'
import { Routes } from 'util/Routes'
import { formatNumberComma, parseNumber } from 'util/NumberUtils'
import React, { Fragment } from 'react'
import numeral from 'numeral'

import { CampaignCustomText } from 'components/campaigns/CampaignCustomText'
import { CampaignDataRoom } from 'components/campaigns/CampaignDataRoom'
import { CampaignEmailOptions, createDateLogicDecorator } from 'components/campaigns/CampaignEmailOptions'
import { CampaignEmailWarning } from 'components/campaigns/CampaignEmailWarning'
import { CampaignHighlights } from 'components/campaigns/CampaignHighlights'
import { CampaignParticipants } from 'components/campaigns/CampaignParticipants'
import { CampaignValuationCard } from './CampaignValuationCard'
import {
  closingTimelineText,
  considerationText,
  fundStructureDetailsText,
  offeringProcessDetailsText,
  offeringProcessText,
  transactionStructureText,
  transactionTimelineText,
} from './CampaignFormattedCustomText'
import moment, { Moment } from 'moment'

export interface CampaignFormShape {
  available_from?: string
  available_to?: string
  num_available_shares?: number
  starting_share_price?: number
  commission?: number

  equifund_id?: number
  min_shares_to_close?: number
  min_listing_size?: number
  min_num_shares?: number
  min_direct_size?: number
  use_custom_closing_timeline: boolean
  custom_closing_timeline?: string

  company_description: string

  valuation_type: string
  last_round_shares?: number
  est_num_company_shares?: number
  custom_contact_message?: string
  custom_contact?: { value: number; label: string }
  custom_valuation_disclaimer?: string
  custom_disclaimer_updated_at?: string
  use_custom_consideration: boolean
  custom_consideration?: string
  use_custom_transaction_timeline: boolean
  custom_transaction_timeline?: string
  use_custom_transaction_structure: boolean
  custom_transaction_structure?: string
  use_custom_fund_structure: boolean
  custom_fund_structure?: string
  use_custom_offering_process: boolean
  custom_offering_process?: string
  use_custom_offering_process_details: boolean
  custom_offering_process_details?: string

  is_reminder_email_checked?: boolean
  is_order_email_checked?: boolean
  is_feedback_email_checked?: boolean
  auto_reminder_email_at?: string
  auto_order_reminder_email_at?: string
  auto_feedback_email_at?: string
}

const useCampaignFormStyles = makeStyles(({ spacing }: Theme) =>
  createStyles({
    formContainer: {
      margin: spacing(0, 2),
    },
  })
)

const validateForm = (values: CampaignFormShape) => {
  let errors: { [key: string]: string } = {}

  const available_from = moment(values.available_from)
  const available_to = moment(values.available_to)

  if (!available_from.isValid()) {
    errors.available_from = 'Start date is not a valid date'
  }

  if (!available_to.isValid()) {
    errors.available_to = 'End date is not a valid date'
  }

  if (available_to.isBefore(available_from)) {
    errors.available_to = 'End date cannot be before start date'
  }

  const num_available_shares = Number(values.num_available_shares)
  if (Number.isNaN(num_available_shares) || !Number.isInteger(num_available_shares) || num_available_shares < 1) {
    errors.num_available_shares = 'Number of shares needs to be a valid integer value'
  }

  const starting_share_price = Number(values.starting_share_price)
  if (Number.isNaN(starting_share_price) || starting_share_price < 0) {
    errors.starting_share_price = 'Share price needs to be a valid number'
  }

  const commission = Number(values.commission)
  if (Number.isNaN(commission) || commission >= 0.1 || commission <= 0) {
    errors.commission = 'Commission should be a valid number between 0 and 10%'
  }

  const min_shares_to_close = Number(values.min_shares_to_close)
  if (Number.isNaN(min_shares_to_close) || !Number.isInteger(min_shares_to_close) || min_shares_to_close < 1) {
    errors.min_shares_to_close = 'Minimum number of shares should be a positive number'
  } else if (min_shares_to_close > num_available_shares) {
    errors.min_shares_to_close = 'Minimum shares to close must be less than available shares'
  }

  const min_listing_size = Number(values.min_listing_size)
  if (Number.isNaN(min_listing_size) || min_listing_size < 0) {
    errors.min_listing_size = 'Minimum bid size should be a valid number'
  } else if (min_listing_size > num_available_shares * starting_share_price) {
    errors.min_listing_size = 'Minimum listing size must be less than total volume'
  }

  const min_direct_size = Number(values.min_direct_size)
  if (!Number.isNaN(min_direct_size) && min_direct_size > num_available_shares * starting_share_price) {
    errors.min_direct_size = 'Minimum direct size must be less than total volume'
  }

  const equifund_id = Number(values.equifund_id)
  if (Number.isNaN(equifund_id) || !Number.isInteger(equifund_id) || equifund_id === 0) {
    errors.equifund_id = 'A fund must be selected'
  }

  switch (values.valuation_type) {
    case ValuationType.CustomShareCount:
      const custom_shares = Number(values.est_num_company_shares)
      if (Number.isNaN(custom_shares) || !Number.isInteger(custom_shares) || custom_shares < 1) {
        errors.est_num_company_shares = 'Minimum number of shares should be a positive number'
      }
      break
    case ValuationType.CustomText:
      if (!values?.custom_contact_message || values?.custom_contact_message?.length < 1) {
        errors.custom_contact_message = 'Please enter a message for custom valuation text'
      }

      if (!Number(values?.custom_contact?.value)) {
        errors.custom_contact = 'Please select a contact for custom valuation'
      }
      break
  }

  return errors
}

export const CampaignForm: React.FC<{ campaignId: string }> = ({ campaignId }) => {
  const classes = useCampaignFormStyles()
  const { data: offeringData, loading } = useGetOfferingQuery({
    variables: { id: campaignId },
  })
  const { data: equifundData } = useGetAllEquifundsQuery()
  const { data: offeringDatesData } = useAllOfferingDatesQuery()
  const [updateOffering] = useUpdateOfferingMutation()

  const offering = offeringData?.offering
  const fundingRound = offering?.company?.last_round ?? null
  const equifunds = equifundData?.allEquifunds.data ?? []
  const capTableIsDisabled = !fundingRound?.est_num_shares
  const defaultValuationType = fundingRound?.est_num_shares ? ValuationType.CapTable : ValuationType.CustomShareCount

  const filteredFunds = equifunds.filter(fund => fund.company_id === offering?.company.id)

  const otherOfferings = (offeringDatesData?.allOfferings.data ?? [])
    .filter(offering => offering.id !== campaignId)
    .sort((a, b) => (a.available_from > b.available_from ? 1 : -1))

  const initialValues = {
    available_from: offering?.available_from,
    available_to: offering?.available_to,
    num_available_shares: offering?.num_available_shares,
    starting_share_price: offering?.starting_share_price,
    commission: offering?.commission ?? 0.05,

    equifund_id: offering?.equifund_id,
    min_shares_to_close: offering?.min_shares_to_close,
    min_listing_size: offering?.min_listing_size,
    min_direct_size: offering?.min_direct_size,
    use_custom_closing_timeline: !!offering?.custom_closing_timeline,
    custom_closing_timeline:
      !!offering?.custom_closing_timeline && offering.custom_closing_timeline
        ? offering.custom_closing_timeline
        : closingTimelineText,
    use_custom_consideration: !!offering?.custom_consideration,
    custom_consideration:
      !!offering?.custom_consideration && offering.custom_consideration
        ? offering.custom_consideration
        : considerationText,
    use_custom_transaction_timeline: !!offering?.custom_transaction_timeline,
    custom_transaction_timeline:
      !!offering?.custom_transaction_timeline && offering.custom_transaction_timeline
        ? offering.custom_transaction_timeline
        : transactionTimelineText,
    use_custom_transaction_structure: !!offering?.custom_transaction_structure,
    custom_transaction_structure:
      !!offering?.custom_transaction_structure && offering.custom_transaction_structure
        ? offering.custom_transaction_structure
        : transactionStructureText,
    use_custom_fund_structure: !!offering?.custom_fund_structure,
    custom_fund_structure:
      !!offering?.custom_fund_structure && offering.custom_fund_structure
        ? offering.custom_fund_structure
        : fundStructureDetailsText,
    use_custom_offering_process: !!offering?.custom_offering_process,
    custom_offering_process:
      !!offering?.custom_offering_process && offering.custom_offering_process
        ? offering.custom_offering_process
        : offeringProcessText,
    use_custom_offering_process_details: !!offering?.custom_offering_process_details,
    custom_offering_process_details:
      !!offering?.custom_offering_process_details && offering.custom_offering_process_details
        ? offering.custom_offering_process_details
        : offeringProcessDetailsText,
    company_description: !loading && (offering?.company_description ?? ''),
    valuation_type:
      offering?.valuation_type === ValuationType.CapTable && capTableIsDisabled
        ? defaultValuationType
        : offering?.valuation_type ?? defaultValuationType,
    last_round_shares: fundingRound && fundingRound.est_num_shares,
    est_num_company_shares: offering?.est_num_company_shares,
    custom_contact_message: offering?.custom_contact_message,
    custom_contact: offering?.custom_contact
      ? { value: offering?.custom_contact?.id, label: offering?.custom_contact?.name }
      : { value: offering?.primary_contacts[0]?.id, label: offering?.primary_contacts[0]?.name },
    custom_valuation_disclaimer: offering?.custom_valuation_disclaimer,
    custom_disclaimer_updated_at: offering?.custom_disclaimer_updated_at,

    is_reminder_email_checked: !!offering?.auto_reminder_email_at,
    is_order_email_checked: !!offering?.auto_order_reminder_email_at,
    is_feedback_email_checked: !!offering?.auto_feedback_email_at,
    auto_reminder_email_at: offering?.auto_reminder_email_at,
    auto_order_reminder_email_at: offering?.auto_order_reminder_email_at,
    auto_feedback_email_at: offering?.auto_feedback_email_at,
  } as CampaignFormShape

  const saveOffering = (values: CampaignFormShape) => {
    const formData = {
      id: campaignId,
      name: offering?.name,
      offering_type: offering?.offering_type ?? '',
      block_type: offering?.block_type,
      equifund_id: values?.equifund_id,
      company_description: values?.company_description,
      num_available_shares: values?.num_available_shares,
      est_num_company_shares:
        values?.valuation_type === ValuationType.CustomShareCount ? values?.est_num_company_shares : null,
      starting_share_price: values?.starting_share_price,
      min_listing_size: Number(values?.min_listing_size ?? 0),
      min_direct_size: values?.min_direct_size ? Number(values?.min_direct_size) : null,
      min_shares_to_close: values?.min_shares_to_close ?? 0,
      available_from: values?.available_from,
      available_to: values?.available_to,
      auto_reminder_email_at: values?.is_reminder_email_checked ? values?.auto_reminder_email_at : null,
      auto_order_reminder_email_at: values?.is_order_email_checked ? values?.auto_order_reminder_email_at : null,
      auto_feedback_email_at: values?.is_feedback_email_checked ? values?.auto_feedback_email_at : null,
      min_num_shares: values?.min_num_shares,
      possible_structures: offering?.possible_structures,
      custom_valuation_disclaimer: values?.custom_valuation_disclaimer,
      custom_closing_timeline: values?.use_custom_closing_timeline ? values?.custom_closing_timeline : null,
      custom_consideration: values?.use_custom_consideration ? values?.custom_consideration : null,
      custom_transaction_timeline: values?.use_custom_transaction_timeline ? values?.custom_transaction_timeline : null,
      custom_transaction_structure: values?.use_custom_transaction_structure
        ? values?.custom_transaction_structure
        : null,
      custom_fund_structure: values?.use_custom_fund_structure ? values?.custom_fund_structure : null,
      custom_offering_process: values?.use_custom_offering_process ? values?.custom_offering_process : null,
      custom_offering_process_details: values?.use_custom_offering_process_details
        ? values?.custom_offering_process_details
        : null,
      commission: Number(values?.commission),
      valuation_type: values?.valuation_type,
      custom_contact_message:
        values?.valuation_type === ValuationType.CustomText ? values?.custom_contact_message : null,
      custom_contact_id: values?.valuation_type === ValuationType.CustomText ? values?.custom_contact?.value : null,
    } as OfferingDetailsFormInput

    return updateOffering({
      variables: {
        form: formData,
      },
    })
  }

  const { enqueueSnackbar } = useSnackbar()
  const sendSnackbarMessage = (message: string) => {
    enqueueSnackbar(message)
  }

  const dateLogicDecorator = createDateLogicDecorator(sendSnackbarMessage)
  return (
    <div className={classes.formContainer}>
      <Form<CampaignFormShape>
        decorators={[dateLogicDecorator]}
        render={props => {
          return (
            <OfferingForm
              campaignId={campaignId}
              offeringToken={offering?.token ?? ''}
              offeringStatus={offering?.status ?? ''}
              offeringCapTable={offering?.company?.last_round}
              filteredFunds={filteredFunds}
              numParticipants={offering?.num_participants ?? 0}
              saveOffering={saveOffering}
              otherOfferings={otherOfferings}
              {...props}
            />
          )
        }}
        onSubmit={() => {}}
        validate={validateForm}
        initialValues={initialValues}
        keepDirtyOnReinitialize
      />
    </div>
  )
}

interface OfferingFormProps extends FormRenderProps<CampaignFormShape> {
  campaignId: string
  offeringToken: string
  offeringStatus: string
  offeringCapTable: Pick<FundingRound, 'stage' | 'valuation' | 'est_num_shares' | 'price_per_share'> | null | undefined
  filteredFunds: BasicFundInfoFragment[]
  numParticipants: number
  otherOfferings: OfferingDateDataFragment[]
  saveOffering: (values: CampaignFormShape) => Promise<any>
}

const useOfferingFormStyles = makeStyles(({ spacing, palette }: Theme) =>
  createStyles({
    actionButtonContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: spacing(2),
      margin: spacing(2),
    },
    fieldGroup: {
      display: 'flex',
      flexWrap: 'wrap',
      padding: spacing(2, 0),
    },
    fieldItem: {
      flex: '0 50%',
      paddingRight: spacing(2),
    },
    fieldItemFullWidth: {
      paddingRight: spacing(2),
    },
    fieldCheck: {
      flex: '0 50%',
      paddingLeft: spacing(2),
      paddingRight: spacing(2),
    },
    textGroup: {
      display: 'flex',
      justifyContent: 'flex-start',
      padding: spacing(2, 0, 0, 0),
    },
    textFieldName: {
      paddingLeft: spacing(2),
      flex: '0 15%',
    },
    textFieldValue: {
      padding: spacing(0, 2),
      flex: '1',
    },
    valuationTypography: {
      paddingTop: spacing(2),
      paddingLeft: spacing(1),
    },
    scheduleFormContainer: {
      display: 'flex',
      justifyContent: 'center',
    },
    placeholder: {
      height: 50,
      background: palette.divider,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: spacing(4),
    },
  })
)

export const isWithin72Hours = (a: Moment, b: Moment) => Math.abs(a.diff(b, 'hours')) < 72

const isPresent = (val: any) => val !== null && val !== undefined
const allPresent = (vals: any[]) => vals.every(isPresent)
const areEqual = (left: any, right: any) => left === right
const allEqual = (vals: any[]) => vals.slice(1).every(val => areEqual(val, vals[0]))
const allPresentAndEqual = (vals: any[]) => allPresent(vals) && allEqual(vals)

const OfferingForm: React.FC<OfferingFormProps> = ({
  values,
  valid,
  campaignId,
  offeringToken,
  offeringStatus,
  offeringCapTable,
  filteredFunds,
  saveOffering,
  otherOfferings,
}) => {
  const offeringStartMoment = moment(values.available_from)
  const offeringReminderMoment = moment(values.auto_reminder_email_at)

  const matchingStartOfferings = otherOfferings.filter(({ available_from }) =>
    allPresentAndEqual([moment(available_from).toISOString(), offeringStartMoment.toISOString()])
  )
  const nearbyStartOfferings = otherOfferings.filter(
    offering =>
      !matchingStartOfferings.includes(offering) &&
      isWithin72Hours(offeringStartMoment, moment(offering.available_from))
  )

  const matchingReminderOfferings = otherOfferings.filter(({ auto_reminder_email_at }) =>
    allPresentAndEqual([moment(auto_reminder_email_at).toISOString(), values.auto_reminder_email_at])
  )
  const nearbyReminderOfferings = otherOfferings.filter(
    offering =>
      !matchingReminderOfferings.includes(offering) &&
      isWithin72Hours(offeringReminderMoment, moment(offering.auto_reminder_email_at))
  )

  const { history } = useRouter()
  const classes = useOfferingFormStyles()

  const [unpublish] = useUnpublishOfferingMutation({
    variables: {
      offeringId: campaignId,
    },
    onCompleted: () => {
      history.push(Routes.campaignForm(campaignId))
    },
  })

  const onPreview = () =>
    saveOffering(values).then(() => (window.location.href = Routes.platformOfferingPage(offeringToken)))

  const onUnPublish = () => {
    unpublish()
  }

  const saveDraft = () => saveOffering(values).then(() => history.push(Routes.campaigns()))

  const [showModal, hideModal] = useModal(() => {
    return (
      <ActionModal
        open
        hideModal={hideModal}
        title="Confirm Save"
        cancelAction={hideModal}
        confirmAction={saveDraft}
        positiveMessage="Confirm"
        negativeMessage="Cancel"
        header="Are you sure?"
        actionMessage={
          <Fragment>
            {nearbyStartOfferings.length > 0 && (
              <Typography variant="body2">
                <CampaignEmailWarning
                  content="Consider combining with the following offerings starting within 72 hours."
                  dateType="invitation"
                  otherOfferings={nearbyStartOfferings}
                />
              </Typography>
            )}
            <Box mt={2} />
            {nearbyReminderOfferings.length > 0 && (
              <Typography variant="body2">
                <CampaignEmailWarning
                  content="Reminders for the following offerings may be combined with this offering's emails."
                  dateType="reminder"
                  otherOfferings={nearbyReminderOfferings}
                />
              </Typography>
            )}
          </Fragment>
        }
      />
    )
  }, [saveDraft, nearbyStartOfferings, nearbyReminderOfferings])

  const onSaveDraft = () => {
    if (nearbyStartOfferings.length === 0 && nearbyReminderOfferings.length === 0) {
      saveDraft()
    } else {
      showModal()
    }
  }

  const fundOptions = filteredFunds.map(fund => {
    return {
      label: fund.name,
      value: fund.id,
    }
  })
  return (
    <form>
      <Card>
        <CardHeader
          title={
            <Typography variant="h6" color="secondary">
              Schedule
            </Typography>
          }
        />
        <CardContent surface="light">
          <div className={classes.scheduleFormContainer}>
            <div className={classes.fieldItem}>
              <DateTimeField name="available_from" label="Start date and time (required)" />
              {nearbyStartOfferings.length > 0 && (
                <HelperText>
                  <CampaignEmailWarning
                    content="Offerings invitations may be combined by setting the same start time. Consider combining the following offerings."
                    dateType="invitation"
                    otherOfferings={nearbyStartOfferings}
                  />
                </HelperText>
              )}
              {matchingStartOfferings.length > 0 && (
                <HelperText>
                  <CampaignEmailWarning
                    content="Offering invitation will be combined into one email with the following offerings."
                    dateType="invitation"
                    otherOfferings={matchingStartOfferings}
                  />
                </HelperText>
              )}
            </div>
            <div className={classes.fieldItem}>
              <DateTimeField name="available_to" label="End date and time (required)" />
            </div>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader
          title={
            <Typography variant="h6" color="secondary">
              Terms
            </Typography>
          }
        />
        <CardContent surface="light">
          <FormSectionTitle>Offering Size</FormSectionTitle>
          <div className={classes.fieldGroup}>
            <div className={classes.fieldItem}>
              <Field
                name="num_available_shares"
                label="Number of shares (required)"
                parse={parseNumber}
                format={formatNumberComma}
                component={TextField}
                fullWidth
              />
            </div>
            <div className={classes.fieldItem}>
              <Field
                name="starting_share_price"
                label="Gross share price (required)"
                component={TextField}
                parse={parseNumber}
                InputProps={{
                  inputComponent: MoneyFormattedInput as any,
                }}
                fullWidth
                helperText={`Total offering amount is ${numeral(
                  Number(values?.starting_share_price) * Number(values?.num_available_shares)
                )
                  .format('($0.00a)')
                  .toUpperCase()} (Gross)`}
              />
            </div>
            <div className={classes.fieldItem}>
              <Field
                name="commission"
                label="Fees (required)"
                component={TextField}
                InputProps={{
                  inputComponent: PercentFormattedInput as any,
                }}
                helperText={`NTB price is ${numeral(
                  Number(values?.starting_share_price) * (1 + Number(values?.commission))
                )
                  .format('($0.00a)')
                  .toUpperCase()}`}
                fullWidth
              />
            </div>
          </div>
          <FormSectionTitle>Requirements</FormSectionTitle>
          <div className={classes.fieldGroup}>
            <div className={classes.fieldItem}>
              <ValueAutocompleteField name="equifund_id" label="Fund (required)" options={fundOptions} />
            </div>
            <div className={classes.fieldItem}>
              <Field
                name="min_shares_to_close"
                label="Minimum shares needed to close (required)"
                component={TextField}
                parse={parseNumber}
                format={formatNumberComma}
                fullWidth
              />
            </div>
            <div className={classes.fieldItem}>
              <Field
                name="min_listing_size"
                label="Minimum bid size (required)"
                component={TextField}
                InputProps={{
                  inputComponent: MoneyFormattedInput as any,
                }}
                fullWidth
              />
            </div>
            <div className={classes.fieldItem}>
              <Field
                name="min_direct_size"
                label="Minimum size for direct transfer"
                component={TextField}
                InputProps={{
                  inputComponent: MoneyFormattedInput as any,
                }}
                fullWidth
              />
            </div>
          </div>
          <CampaignCustomText
            name="custom_consideration"
            label="Use Custom Consideration Text"
            showCustomText={values.use_custom_consideration}
          />
          <CampaignCustomText
            name="custom_transaction_timeline"
            label="Use Custom Transaction Timeline Text"
            showCustomText={values.use_custom_transaction_timeline}
          />
          <CampaignCustomText
            name="custom_closing_timeline"
            label="Use Custom Closing Timeline Text"
            showCustomText={values.use_custom_closing_timeline}
          />
          <CampaignCustomText
            name="custom_transaction_structure"
            label="Use Custom Transaction Structure Text"
            showCustomText={values.use_custom_transaction_structure}
          />
          <CampaignCustomText
            name="custom_fund_structure"
            label="Use Custom Fund Structure Text"
            showCustomText={values.use_custom_fund_structure}
          />
          <CampaignCustomText
            name="custom_offering_process"
            label="Use Custom Offering Process Text"
            showCustomText={values.use_custom_offering_process}
          />
        </CardContent>
      </Card>

      <CampaignValuationCard offeringCapTable={offeringCapTable} />

      {(!!values.company_description || values.company_description === '') && <CampaignHighlights />}

      <CampaignDataRoom offeringId={campaignId} />

      <Card>
        <CardHeader
          title={
            <Typography variant="h6" color="secondary">
              Custom Offering Process Details
            </Typography>
          }
        />
        <CardContent surface="light">
          <CampaignCustomText
            name="custom_offering_process_details"
            label="Use Custom Offering Process Details Text"
            showCustomText={values.use_custom_offering_process_details}
          />
        </CardContent>
      </Card>

      <CampaignEmailOptions
        campaignId={campaignId}
        nearbyReminderOfferings={nearbyReminderOfferings}
        matchingReminderOfferings={matchingReminderOfferings}
      />

      <CampaignParticipants offeringId={campaignId} />

      <div className={classes.actionButtonContainer}>
        <Button onClick={() => history.push(Routes.campaigns())}>Cancel</Button>
        <Button onClick={onSaveDraft} disabled={offeringStatus !== 'Draft'}>
          Save Draft
        </Button>
        {offeringStatus === 'Draft' && (
          <Button color="primary" onClick={onPreview} disabled={!valid}>
            Preview
          </Button>
        )}
        {offeringStatus === 'Upcoming' && (
          <Button color="primary" onClick={onUnPublish} disabled={!valid}>
            Un-publish
          </Button>
        )}
      </div>
    </form>
  )
}
